import { defineStore } from 'pinia'
import axiosOverlayConnector from '@/services/axiosOverlayConnector.js'
import useUserStore from '@/stores/user.js'
import useDepenseStore from '@/stores/depense.js'

export default defineStore('categorieDepense', {
  state: () => ({
    categorieDepenses: null,
    loading: 0
  }),

  getters: {
    isLoading: (state) => {
      return state.loading > 0
    }
  },

  actions: {
    addDepenseToCategorie(categorieId, depense) {
      if (!this.categorieDepenses) return

      const foundCategorie = this.categorieDepenses.find((categorie) => {
        return categorie.idCategorie === categorieId
      })

      // Ajout de la dépense à la catégorie correspondante
      if (foundCategorie) {
        foundCategorie.depenses.unshift(depense)
        foundCategorie.totalMontant += depense.montant
        this.categorieDepenses = [...this.categorieDepenses]
      }
    },

    removeDepenseFromCategorie(categorieId, depenseId) {
      if (!this.categorieDepenses) return

      const foundCategorie = this.categorieDepenses.find((categorie) => {
        return categorie.idCategorie === categorieId
      })

      // Suppression de la dépense à la catégorie correspondante
      if (foundCategorie) {
        // Update montant total
        const depenseRemoved = foundCategorie.depenses.find((depense) => depense.id === depenseId)
        if (depenseRemoved) {
          foundCategorie.totalMontant -= depenseRemoved.montant
        }

        foundCategorie.depenses = foundCategorie.depenses.filter(
          (depense) => depense.id !== depenseId
        )

        this.categorieDepenses = [...this.categorieDepenses]
      }
    },

    removeCategorieDepense(idCategorie) {
      if (!this.categorieDepenses) return

      this.categorieDepenses = this.categorieDepenses.filter(
        (item) => item.idCategorie !== idCategorie
      )
    },

    addNewCategorieDepense(categorie) {
      if (!this.categorieDepenses) {
        this.categorieDepenses = []
      }

      this.categorieDepenses.push({
        couleur: categorie.couleur,
        depenses: [],
        icone: categorie.icone,
        idCategorie: categorie.id,
        libelle: categorie.libelle,
        limite: categorie.limite,
        montantTotal: 0
      })
    },

    updateCategorieDepense(categorie) {
      if (!this.categorieDepenses) return

      const index = this.categorieDepenses.findIndex(
        (item) => item.idCategorie === categorie.idCategorie
      )
      if (index !== -1) {
        this.categorieDepenses[index] = {
          ...this.categorieDepenses[index],
          depenses: [...categorie.depenses]
        }
      }
    },

    loadCategorieDepenses() {
      this.loading++

      return new Promise((resolve, reject) => {
        let url = import.meta.env.VITE_API_URL + '/categorie-depenses'

        const config = {
          url: url,
          method: 'GET'
        }

        return axiosOverlayConnector(config)
          .then((response) => {
            this.categorieDepenses = response.data

            resolve()
          })
          .catch((error) => {
            reject(error)
          })
          .finally(() => {
            this.loading--
          })
      })
    },

    async addMultipleDepenses(depenseInfoList) {
      const depenseStore = useDepenseStore()

      this.loading++

      const url = import.meta.env.VITE_API_URL + '/depenses/multiple'
      const config = {
        url,
        method: 'POST',
        data: { depenses: depenseInfoList }
      }

      try {
        const response = await axiosOverlayConnector(config)
        const results = response.data

        // Rafraîchir le dashboard une seule fois
        this.loadCategorieDepenses()
        depenseStore.loadDepenseByLast12Month()

        return results
      } finally {
        this.loading--
      }
    },

    addDepense(depenseInfo) {
      const userStore = useUserStore()
      const depenseStore = useDepenseStore()

      this.loading++

      return new Promise((resolve, reject) => {
        let url = import.meta.env.VITE_API_URL + '/depenses'

        const config = {
          url: url,
          method: 'POST',
          data: {
            ...depenseInfo,
            idCompte: userStore.idCompte
          }
        }

        return axiosOverlayConnector(config)
          .then(() => {
            this.loadCategorieDepenses()

            // Rafraîchir la liste des dépenses de la catégorie si elle est déjà chargée
            if (depenseStore.depenseByOneCategorie !== null) {
              depenseStore.loadDepenseByOneCategorie(depenseInfo.idCategorie)
            }

            // Update charts
            // Récupérer le mois et l'année de la dépense ajoutée
            const depenseDate = new Date(Number(depenseInfo.date) * 1000)
            const depenseMonth = depenseDate.getMonth() + 1
            const depenseYear = depenseDate.getFullYear()

            // Trouver l'index correspondant dans depenseByLast12Month
            const monthIndex = depenseStore.getDepenseByLast12MonthIndex(depenseMonth, depenseYear)

            // Update chart si le mois existe dans la liste
            if (monthIndex !== -1) {
              const currentTotal = depenseStore.depenseByLast12Month[monthIndex].totalMontant
              const newMontant = currentTotal + depenseInfo.montant
              depenseStore.updateDepenseByLast12MonthTotalMontant(monthIndex, newMontant)
            }

            // Update chart détails catégorie si la liste existe
            if (
              depenseStore.depenseByOneCategorieSum &&
              depenseStore.depenseByOneCategorieSum.length > 0
            ) {
              const monthIndexCategorie = depenseStore.getDepenseByOneCategorieIndex(
                depenseMonth,
                depenseYear
              )

              if (monthIndexCategorie !== -1) {
                const currentTotalCategorie =
                  depenseStore.depenseByOneCategorieSum[monthIndexCategorie].totalMontant
                const newMontantCategorie = currentTotalCategorie + depenseInfo.montant
                depenseStore.updateDepenseByOneCategorieSum(
                  monthIndexCategorie,
                  newMontantCategorie
                )
              }
            }

            resolve('success')
          })
          .catch((error) => {
            // console.log(apiErrorParser(error))
            reject(error)
          })
          .finally(() => {
            this.loading--
          })
      })
    }
  }
})
